import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Heart,
  Sparkles,
  Target,
  MessageSquare,
  CheckCircle2,
  Pencil,
  RotateCcw,
  Plus,
  Trash2,
  UserPlus,
  Save,
  Loader2,
  HelpCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";

const SECTION_META = {
  loved_well: { label: "Love Assessment", icon: Heart, color: "text-red-500", page: 1 },
  fruits_of_spirit: { label: "Fruits of the Spirit", icon: Sparkles, color: "text-amber-500", page: 2 },
  glorifying_god: { label: "Glorifying God", icon: Target, color: "text-blue-500", page: 3 },
  reflection: { label: "Reflection", icon: MessageSquare, color: "text-purple-500", page: 4 },
  honesty: { label: "Honesty Check", icon: CheckCircle2, color: "text-green-500", page: 5 },
};

const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Short Text" },
  { value: "textarea", label: "Long Text" },
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "checkbox", label: "Checkbox" },
  { value: "rating", label: "Rating (1-10)" },
  { value: "yes_no", label: "Yes / No" },
  { value: "number", label: "Number" },
];

// ---- Field Edit Dialog ----
function FieldEditDialog({ field, open, onClose, onSave }) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (field) {
      setLabel(field.label || "");
      setDescription(field.description || "");
    }
  }, [field]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(field.field_key, {
        custom_label: label || null,
        custom_description: description,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/checkin-fields/${field.field_key}`);
      toast.success("Reset to default");
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!field) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4" />
            Edit Field
          </DialogTitle>
          <DialogDescription>
            Modify the label and description for &quot;{field.original_label}&quot;
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Label Text</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={field.original_label} />
            <p className="text-xs text-slate-400">Original: {field.original_label}</p>
          </div>
          <div className="space-y-2">
            <Label>Help / Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={field.original_description || "No description"} rows={2} />
            {field.original_description && <p className="text-xs text-slate-400">Original: {field.original_description}</p>}
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          {field.has_override && (
            <Button variant="outline" size="sm" onClick={handleReset} disabled={saving}>
              <RotateCcw className="w-3 h-3 mr-1" /> Reset
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Custom Question Dialog ----
function CustomQuestionDialog({ question, students, open, onClose, onSave }) {
  const [studentId, setStudentId] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [isRequired, setIsRequired] = useState(true);
  const [helpText, setHelpText] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [optionsText, setOptionsText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (question) {
      setStudentId(question.student_id || "");
      setQuestionText(question.question_text || "");
      setFieldType(question.field_type || "text");
      setIsRequired(question.is_required !== false);
      setHelpText(question.help_text || "");
      setPlaceholder(question.placeholder || "");
      setOptionsText(question.options?.choices?.join("\n") || "");
    } else {
      setStudentId("");
      setQuestionText("");
      setFieldType("text");
      setIsRequired(true);
      setHelpText("");
      setPlaceholder("");
      setOptionsText("");
    }
  }, [question, open]);

  const handleSave = async () => {
    if (!questionText.trim()) {
      toast.error("Question text is required");
      return;
    }
    if (!question && !studentId) {
      toast.error("Please select a student");
      return;
    }

    setSaving(true);
    try {
      const options = ["multiple_choice", "checkbox"].includes(fieldType) && optionsText.trim()
        ? { choices: optionsText.split("\n").map((s) => s.trim()).filter(Boolean) }
        : fieldType === "rating"
        ? { scale: 10 }
        : null;

      await onSave({
        student_id: question ? question.student_id : studentId,
        question_text: questionText,
        field_type: fieldType,
        is_required: isRequired,
        help_text: helpText || null,
        placeholder: placeholder || null,
        options,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {question ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {question ? "Edit Custom Question" : "New Custom Question"}
          </DialogTitle>
          <DialogDescription>
            {question
              ? "Edit this custom question for the student."
              : "Create a custom question for a specific student. It will appear as an extra step in their check-in."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {!question && (
            <div className="space-y-2">
              <Label>Student</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a student" />
                </SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Question Text</Label>
            <Textarea value={questionText} onChange={(e) => setQuestionText(e.target.value)} placeholder="Enter your question..." rows={2} />
          </div>
          <div className="space-y-2">
            <Label>Answer Type</Label>
            <Select value={fieldType} onValueChange={setFieldType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {["multiple_choice", "checkbox"].includes(fieldType) && (
            <div className="space-y-2">
              <Label>Options (one per line)</Label>
              <Textarea value={optionsText} onChange={(e) => setOptionsText(e.target.value)} placeholder={"Option A\nOption B\nOption C"} rows={3} />
            </div>
          )}
          <div className="space-y-2">
            <Label>Placeholder text (optional)</Label>
            <Input value={placeholder} onChange={(e) => setPlaceholder(e.target.value)} placeholder="Placeholder..." />
          </div>
          <div className="space-y-2">
            <Label>Help text (optional)</Label>
            <Input value={helpText} onChange={(e) => setHelpText(e.target.value)} placeholder="Additional guidance..." />
          </div>
          <div className="flex items-center justify-between">
            <Label>Required</Label>
            <Switch checked={isRequired} onCheckedChange={setIsRequired} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {question ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Main Page ----
export default function CheckInFieldEditor({ user }) {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editField, setEditField] = useState(null);
  const [editQuestion, setEditQuestion] = useState(null);
  const [showNewQuestion, setShowNewQuestion] = useState(false);
  const [filterStudent, setFilterStudent] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const [fieldsRes, questionsRes, studentsRes] = await Promise.all([
        axios.get(`${API}/checkin-fields`),
        axios.get(`${API}/custom-questions`),
        axios.get(`${API}/users/students`),
      ]);
      setFields(fieldsRes.data);
      setCustomQuestions(questionsRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== "teacher") {
      navigate("/student");
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const handleSaveField = async (fieldKey, data) => {
    await axios.put(`${API}/checkin-fields/${fieldKey}`, data);
    toast.success("Field updated");
    fetchData();
  };

  const handleSaveQuestion = async (data) => {
    if (editQuestion) {
      await axios.put(`${API}/custom-questions/${editQuestion.id}`, data);
      toast.success("Question updated");
    } else {
      await axios.post(`${API}/custom-questions`, data);
      toast.success("Question created");
    }
    setEditQuestion(null);
    setShowNewQuestion(false);
    fetchData();
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const confirmDeleteQuestion = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/custom-questions/${deleteTarget}`);
      toast.success("Question removed");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete question");
    }
    setDeleteTarget(null);
  };

  // Group fields by section
  const fieldsBySection = Object.entries(SECTION_META).map(([section, meta]) => ({
    section,
    ...meta,
    fields: fields.filter((f) => f.section === section),
  }));

  const filteredQuestions = filterStudent === "all"
    ? customQuestions
    : customQuestions.filter((q) => q.student_id === filterStudent);

  // Group questions by student
  const questionsByStudent = {};
  filteredQuestions.forEach((q) => {
    if (!questionsByStudent[q.student_id]) {
      const student = students.find((s) => s.user_id === q.student_id);
      questionsByStudent[q.student_id] = { name: student?.name || q.student_id, questions: [] };
    }
    questionsByStudent[q.student_id].questions.push(q);
  });

  if (user?.role !== "teacher") return null;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/checkins")} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">Check-In Editor</h1>
            <p className="text-slate-600 mt-1">Modify check-in content and manage custom student questions</p>
          </div>
        </div>

        <Tabs defaultValue="fields" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
            <TabsTrigger value="fields">
              <Pencil className="w-4 h-4 mr-1" /> Edit Fields
            </TabsTrigger>
            <TabsTrigger value="custom">
              <UserPlus className="w-4 h-4 mr-1" /> Custom Questions
            </TabsTrigger>
          </TabsList>

          {/* Tab 1: Edit Existing Fields */}
          <TabsContent value="fields" className="space-y-6 mt-4">
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 flex items-start gap-3">
                <HelpCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800">
                  Edit the label and description of existing check-in fields. The page structure, field order, and navigation remain unchanged.
                </p>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
              </div>
            ) : (
              fieldsBySection.map(({ section, label, icon: Icon, color, fields: sectionFields }) => (
                <Card key={section}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${color}`} />
                      Step {SECTION_META[section].page}: {label}
                    </CardTitle>
                    <CardDescription>{sectionFields.length} field{sectionFields.length !== 1 ? "s" : ""}</CardDescription>
                  </CardHeader>
                  <CardContent className="divide-y divide-slate-100">
                    {sectionFields.map((field) => (
                      <div key={field.field_key} className="flex items-center justify-between py-3 gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 truncate">{field.label}</p>
                          {field.description && (
                            <p className="text-xs text-slate-500 truncate">{field.description}</p>
                          )}
                          {field.has_override && (
                            <Badge variant="outline" className="mt-1 text-xs text-amber-600 border-amber-300">Modified</Badge>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setEditField(field)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Tab 2: Custom Student Questions */}
          <TabsContent value="custom" className="space-y-4 mt-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <Select value={filterStudent} onValueChange={setFilterStudent}>
                <SelectTrigger className="w-full sm:w-[250px]">
                  <SelectValue placeholder="Filter by student" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Students</SelectItem>
                  {students.map((s) => (
                    <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => setShowNewQuestion(true)} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-1" /> New Question
              </Button>
            </div>

            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4 flex items-start gap-3">
                <HelpCircle className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-sm text-blue-800">
                  Custom questions appear as an extra step at the end of a student&apos;s check-in. Only the assigned student sees them.
                </p>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
              </div>
            ) : Object.keys(questionsByStudent).length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No custom questions assigned yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowNewQuestion(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Create First Question
                  </Button>
                </CardContent>
              </Card>
            ) : (
              Object.entries(questionsByStudent).map(([studentId, { name, questions }]) => (
                <Card key={studentId}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{name}</CardTitle>
                    <CardDescription>{questions.length} custom question{questions.length !== 1 ? "s" : ""}</CardDescription>
                  </CardHeader>
                  <CardContent className="divide-y divide-slate-100">
                    {questions.map((q) => (
                      <div key={q.id} className="flex items-center justify-between py-3 gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900">{q.question_text}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{FIELD_TYPE_OPTIONS.find((o) => o.value === q.field_type)?.label || q.field_type}</Badge>
                            {q.is_required && <Badge className="text-xs bg-red-100 text-red-700">Required</Badge>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setEditQuestion(q)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => setDeleteTarget(q.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <FieldEditDialog
          field={editField}
          open={!!editField}
          onClose={() => { setEditField(null); fetchData(); }}
          onSave={handleSaveField}
        />
        <CustomQuestionDialog
          question={editQuestion || (showNewQuestion ? null : undefined)}
          students={students}
          open={!!editQuestion || showNewQuestion}
          onClose={() => { setEditQuestion(null); setShowNewQuestion(false); }}
          onSave={handleSaveQuestion}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteTarget !== null} onOpenChange={() => setDeleteTarget(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-500" />
                Remove Question
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to remove this custom question? Previously submitted responses will be preserved.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="destructive" onClick={confirmDeleteQuestion}>Remove</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
